import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import content from "@/data/productContent.json";

// Map from live product slug → the "name" prefix used in the content JSON.
// Only slugs listed here get their compound-specific content rendered.
const SLUG_MATCH = {
  // Raw Vials
  "nad-plus": "NAD+",
  "ipamorelin": "Ipamorelin",
  "ghk-cu": "GHK-Cu",
  "mots-c": "MOTS-C",
  "melanotan-1": "Melanotan I",
  "melanotan-2": "Melanotan II",
  "kpv": "KPV",
  "tirzepatide-vial": "Tirzepatide",
  "semaglutide-vial": "Semaglutide",
  "cagrilintide": "Cagrilintide",
  "dsip": "DSIP",
  "epithalon": "Epithalon",
  "pt-141": "PT-141",
  "cjc-1295-dac": "CJC-1295 with DAC",
  "cjc-1295-no-dac": "CJC-1295 without DAC",
  "aod-9604": "AOD-9604",
  "selank": "Selank",
  "semax": "Semax",
  "tb-500": "TB-500",
  "bpc-157": "BPC-157",
  "retatrutide": "Retatrutide",
  "glow": "GLOW 70",
  "klow": "KLOW",
  "hulk": "HULK",
  "wolverine": "WOLVERINE",
  // Pen Kits
  "retatrutide-pen": "Retatrutide 40mg Pen Kit",
  "bpc-157-pen": "BPC-157 20mg Pen Kit",
  "tb-500-pen": "TB-500 20mg Pen Kit",
  "cjc-1295-pen": "CJC-1295 (w/o DAC) 10mg Pen Kit",
  "ghk-cu-pen": "GHK-Cu 50mg Pen Kit",
  "mots-c-pen": "MOTS-c 20mg Pen Kit",
  "nad-plus-pen": "NAD+ 500mg Pen Kit",
  "pt-141-pen": "PT-141 20mg Pen Kit",
  "glow-pen": "GLOW Blend 70mg Pen Kit",
  "hulk-pen": "HULK Blend 10mg Pen Kit",
  "klow-pen": "KLOW Blend 80mg Pen Kit",
  "wolverine-pen": "Wolverine Blend 20mg Pen Kit",
};

// Per-slug PDP structured-content overrides. Only compounds that have been
// through the individual SEO/copy pass are listed. Every other slug renders
// the default section titles ("Description", "Research Context") and no
// extra sections. Add entries here narrowly, per compound task authorised.
const PDP_CONTENT_OVERRIDES = {
  "bpc-157": {
    descriptionHeading: "About BPC-157",
    researchContextHeading: "Preclinical Research Context",
    references: [
      {
        title: "BPC-157 · PubChem CID 9941957",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/9941957",
      },
      {
        title: "Pentadecapeptide BPC 157 and the Central Nervous System",
        source: "PubMed · Sikiric et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/34940100/",
      },
      {
        title: "Stable Gastric Pentadecapeptide BPC 157 as a Therapy",
        source: "PubMed · Sikiric et al. (review)",
        url: "https://pubmed.ncbi.nlm.nih.gov/32330143/",
      },
      {
        title: "BPC 157 and Its Effects on Wound Healing and Angiogenesis",
        source: "PubMed · Chang et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/34681714/",
      },
    ],
    relatedCompounds: [
      { slug: "tb-500", label: "TB-500" },
      { slug: "glow", label: "GLOW" },
      { slug: "wolverine", label: "Wolverine" },
    ],
    relatedLearn: [
      { to: "/learn/bpc-157", label: "What is BPC-157?" },
      { to: "/learn/bpc-157-vs-tb-500", label: "BPC-157 vs TB-500" },
      { to: "/learn/glow-vs-klow", label: "GLOW vs KLOW" },
    ],
  },
  "klow": {
    descriptionHeading: "About KLOW",
    researchContextHeading: "Preclinical Research Context",
    showComposition: true,
    ingredientContext: [
      {
        slug: "ghk-cu",
        name: "GHK-Cu",
        body: "A copper-binding tripeptide investigated in experimental research involving signalling and extracellular-matrix-related biological processes.",
      },
      {
        slug: "bpc-157",
        name: "BPC-157",
        body: "A synthetic peptide investigated predominantly in preclinical research involving angiogenesis-related signalling, nitric-oxide pathways and experimental tissue models.",
      },
      {
        slug: "tb-500",
        name: "TB-500",
        body: "A research peptide associated with thymosin beta-4-related experimental research.",
      },
      {
        slug: "kpv",
        name: "KPV",
        body: "A short peptide sequence investigated experimentally in cellular and inflammatory-pathway research.",
      },
    ],
    references: [
      {
        title: "GHK-Cu, Skin regenerative and anti-ageing properties of the tripeptide",
        source: "PubMed · Pickart & Margolina",
        url: "https://pubmed.ncbi.nlm.nih.gov/29738435/",
      },
      {
        title: "BPC-157, Stable Gastric Pentadecapeptide BPC 157 as a Therapy",
        source: "PubMed · Sikiric et al. (review)",
        url: "https://pubmed.ncbi.nlm.nih.gov/32330143/",
      },
      {
        title: "BPC-157, Effects on Wound Healing and Angiogenesis",
        source: "PubMed · Chang et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/34681714/",
      },
      {
        title: "TB-500 (Thymosin β4) · PubChem CID 16130571",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/16130571",
      },
      {
        title: "Thymosin beta-4: Actin-sequestering protein moonlights to repair injured tissues",
        source: "PubMed · Goldstein et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/15922731/",
      },
      {
        title: "KPV, anti-inflammatory tripeptide, cellular signalling review",
        source: "PubMed · Kannengiesser et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/18272200/",
      },
    ],
    relatedCompounds: [
      { slug: "glow", label: "GLOW" },
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "ghk-cu", label: "GHK-Cu" },
      { slug: "tb-500", label: "TB-500" },
      { slug: "kpv", label: "KPV" },
    ],
    relatedLearn: [
      { to: "/learn/glow-vs-klow", label: "GLOW vs KLOW" },
      { to: "/learn/peptide-blends", label: "What are Peptide Blends?" },
    ],
  },
  "klow-pen": {
    compoundLabel: "KLOW Pen Kit",
    descriptionHeading: "About the KLOW Pen Kit",
    researchContextHeading: "Ingredient Research Context",
    showComposition: true,
    hidePenHowItWorks: true,
    hidePenFormatFaq: true,
    penFormat: [
      {
        title: "Pre-filled",
        body: "Prepared pen format; no Raw Vial reconstitution step.",
      },
      {
        title: "Dial-controlled",
        body: "Mechanical dial system for controlled laboratory dispensing.",
      },
      {
        title: "Compatible hardware",
        body: "Designed for the compatible Pen Kit hardware and consumables listed by PepDirect.",
      },
      {
        title: "Cold storage",
        body: "Follow the verified storage conditions displayed in the product specifications.",
      },
    ],
    ingredientContext: [
      {
        slug: "ghk-cu",
        name: "GHK-Cu",
        body: "A copper-binding tripeptide investigated in experimental research involving signalling and extracellular-matrix-related biological processes.",
      },
      {
        slug: "bpc-157",
        name: "BPC-157",
        body: "A synthetic peptide investigated predominantly in preclinical research involving angiogenesis-related signalling and nitric-oxide pathways.",
      },
      {
        slug: "tb-500",
        name: "TB-500",
        body: "A research peptide associated with thymosin beta-4-related experimental research.",
      },
      {
        slug: "kpv",
        name: "KPV",
        body: "A short peptide sequence investigated experimentally in cellular and inflammatory-pathway research.",
      },
    ],
    relatedCompounds: [
      { slug: "klow", label: "KLOW Raw Vial" },
      { slug: "ghk-cu", label: "GHK-Cu" },
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "tb-500", label: "TB-500" },
      { slug: "kpv", label: "KPV" },
    ],
    relatedLearn: [
      { to: "/learn/glow-vs-klow", label: "GLOW vs KLOW" },
      { to: "/learn/peptide-blends", label: "What are Peptide Blends?" },
    ],
  },
  "glow": {
    compoundLabel: "GLOW",
    descriptionHeading: "About GLOW",
    researchContextHeading: "Ingredient Research Context",
    showComposition: true,
    ingredientContext: [
      {
        slug: "ghk-cu",
        name: "GHK-Cu",
        body: "A copper-binding tripeptide investigated in experimental research involving signalling and extracellular-matrix-related biological processes.",
      },
      {
        slug: "bpc-157",
        name: "BPC-157",
        body: "A synthetic peptide investigated predominantly in preclinical research involving angiogenesis-related signalling and nitric-oxide pathways.",
      },
      {
        slug: "tb-500",
        name: "TB-500",
        body: "A research peptide associated with thymosin beta-4-related experimental research.",
      },
    ],
    references: [
      {
        title: "GHK-Cu, Skin regenerative and anti-ageing properties of the tripeptide",
        source: "PubMed · Pickart & Margolina",
        url: "https://pubmed.ncbi.nlm.nih.gov/29738435/",
      },
      {
        title: "GHK-Cu · PubChem CID 73587",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/73587",
      },
      {
        title: "BPC-157, Stable Gastric Pentadecapeptide BPC 157 as a Therapy",
        source: "PubMed · Sikiric et al. (review)",
        url: "https://pubmed.ncbi.nlm.nih.gov/32330143/",
      },
      {
        title: "BPC-157, Effects on Wound Healing and Angiogenesis",
        source: "PubMed · Chang et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/34681714/",
      },
      {
        title: "TB-500 (Thymosin β4) · PubChem CID 16130571",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/16130571",
      },
      {
        title: "Thymosin beta-4: Actin-sequestering protein moonlights to repair injured tissues",
        source: "PubMed · Goldstein et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/15922731/",
      },
    ],
    relatedCompounds: [
      { slug: "ghk-cu", label: "GHK-Cu" },
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "tb-500", label: "TB-500" },
      { slug: "klow", label: "KLOW" },
    ],
    relatedLearn: [
      { to: "/learn/glow-vs-klow", label: "GLOW vs KLOW" },
      { to: "/learn/peptide-blends", label: "What are Peptide Blends?" },
    ],
  },
  "glow-pen": {
    compoundLabel: "GLOW Pen Kit",
    descriptionHeading: "About the GLOW Pen Kit",
    researchContextHeading: "Ingredient Research Context",
    showComposition: true,
    hidePenHowItWorks: true,
    hidePenFormatFaq: true,
    penFormat: [
      {
        title: "Pre-filled",
        body: "Prepared pen format; no Raw Vial reconstitution step.",
      },
      {
        title: "Dial-controlled",
        body: "Mechanical dial system for controlled laboratory dispensing.",
      },
      {
        title: "Compatible hardware",
        body: "Designed for compatible Pen Kit hardware and consumables listed by PepDirect.",
      },
      {
        title: "Cold storage",
        body: "Follow the verified storage conditions displayed in the product specifications.",
      },
    ],
    ingredientContext: [
      {
        slug: "ghk-cu",
        name: "GHK-Cu",
        body: "A copper-binding tripeptide investigated in experimental research involving signalling and extracellular-matrix-related biological processes.",
      },
      {
        slug: "bpc-157",
        name: "BPC-157",
        body: "A synthetic peptide investigated predominantly in preclinical research involving angiogenesis-related signalling and nitric-oxide pathways.",
      },
      {
        slug: "tb-500",
        name: "TB-500",
        body: "A research peptide associated with thymosin beta-4-related experimental research.",
      },
    ],
    relatedCompounds: [
      { slug: "glow", label: "GLOW Raw Vial" },
      { slug: "ghk-cu", label: "GHK-Cu" },
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "tb-500", label: "TB-500" },
    ],
    relatedLearn: [
      { to: "/learn/glow-vs-klow", label: "GLOW vs KLOW" },
      { to: "/learn/peptide-blends", label: "What are Peptide Blends?" },
    ],
  },
  "ghk-cu": {
    compoundLabel: "GHK-Cu",
    descriptionHeading: "About GHK-Cu",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    extraSection: {
      heading: "GHK and GHK-Cu",
      body: "GHK is the copper-free tripeptide glycyl-L-histidyl-L-lysine. GHK-Cu refers to its copper complex. These are related but chemically distinct research entities, so identifiers and evidence should not be treated as interchangeable. Research conducted on GHK alone should not automatically be represented as research on the copper complex, and vice versa.",
    },
    references: [
      {
        title: "GHK-Cu / Prezatide Copper · PubChem CID 71587328 (CAS 89030-95-5)",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/71587328",
      },
      {
        title: "GHK · PubChem CID 73587 (free tripeptide, copper-free)",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/73587",
      },
      {
        title: "GHK-Cu, Skin regenerative and anti-ageing properties of the tripeptide (review)",
        source: "PubMed · Pickart & Margolina",
        url: "https://pubmed.ncbi.nlm.nih.gov/29738435/",
      },
      {
        title: "The Human Tripeptide GHK-Cu in Prevention of Oxidative Stress and Degenerative Conditions",
        source: "PubMed · Pickart, Vasquez-Soltero, Margolina",
        url: "https://pubmed.ncbi.nlm.nih.gov/22580375/",
      },
      {
        title: "GHK Peptide as a Natural Modulator of Multiple Cellular Pathways in Skin Regeneration (studies GHK, not the copper complex)",
        source: "PubMed · Pickart & Margolina",
        url: "https://pubmed.ncbi.nlm.nih.gov/26290989/",
      },
      {
        title: "GHK-Cu, copper transport, gene expression and tissue-repair research overview",
        source: "PubMed · Pickart",
        url: "https://pubmed.ncbi.nlm.nih.gov/18505499/",
      },
    ],
    relatedCompounds: [
      { slug: "glow", label: "GLOW" },
      { slug: "klow", label: "KLOW" },
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "tb-500", label: "TB-500" },
    ],
    relatedLearn: [
      { to: "/learn/ghk-cu", label: "What is GHK-Cu?" },
      { to: "/learn/glow-vs-klow", label: "GLOW vs KLOW" },
    ],
  },
  "kpv": {
    compoundLabel: "KPV",
    descriptionHeading: "About KPV",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    references: [
      {
        title: "KPV · PubChem CID 125672 (Lys-Pro-Val tripeptide)",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/125672",
      },
      {
        title: "α-MSH C-terminal tripeptide KPV, anti-inflammatory tripeptide, cellular signalling review",
        source: "PubMed · Kannengiesser et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/18272200/",
      },
      {
        title: "Uptake of the anti-inflammatory tripeptide KPV in intestinal epithelial cells via PepT1",
        source: "PubMed · Dalmasso et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/18191621/",
      },
      {
        title: "Melanocortin-derived tripeptide KPV attenuates inflammation in preclinical models",
        source: "PubMed · Brzoska et al. (review)",
        url: "https://pubmed.ncbi.nlm.nih.gov/18477712/",
      },
      {
        title: "KPV, cellular NF-κB pathway research overview",
        source: "PubMed · Ceriani, Diaz, Catania",
        url: "https://pubmed.ncbi.nlm.nih.gov/8033988/",
      },
    ],
    relatedCompounds: [
      { slug: "klow", label: "KLOW" },
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "tb-500", label: "TB-500" },
      { slug: "ghk-cu", label: "GHK-Cu" },
    ],
    relatedLearn: [
      { to: "/learn/kpv", label: "What is KPV?" },
      { to: "/learn/glow-vs-klow", label: "GLOW vs KLOW" },
    ],
  },
  "semax": {
    compoundLabel: "Semax",
    descriptionHeading: "About Semax",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    references: [
      {
        title: "Semax · PubChem CID 9811102 (heptapeptide Met-Glu-His-Phe-Pro-Gly-Pro)",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/9811102",
      },
      {
        title: "Semax, an ACTH(4-10) analogue, induces BDNF and trkB mRNA in the rat basal forebrain (animal study)",
        source: "PubMed · Dolotov et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/16899073/",
      },
      {
        title: "Neurotrophin (BDNF/NGF) expression research in rat glial cultures (cellular study)",
        source: "PubMed · Shadrina et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/11599023/",
      },
      {
        title: "Peptide preparation Semax attenuates experimental brain ischaemia (rodent model)",
        source: "PubMed · Ashmarin, Nezavibatko et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/16193200/",
      },
      {
        title: "Semax and its C-terminal tripeptide PGP, genome-wide expression research (cellular/animal)",
        source: "PubMed · Medvedeva et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/25330133/",
      },
    ],
    relatedCompounds: [
      { slug: "selank", label: "Selank" },
    ],
    relatedLearn: [
      { to: "/learn/semax", label: "What is Semax?" },
      { to: "/learn/semax-vs-selank", label: "Semax vs Selank" },
    ],
  },
  "nad-plus-pen": {
    compoundLabel: "NAD+ Pen Kit",
    hidePenHowItWorks: true,
    hidePenFormatFaq: true,
    penFormat: [
      {
        title: "Pre-filled",
        body: "Prepared in a pre-filled research format rather than lyophilised raw powder.",
      },
      {
        title: "Dial-controlled",
        body: "Fixed click-increment mechanism for consistent measured dispensing.",
      },
      {
        title: "Pen device",
        body: "Pen-format presentation distinct from raw vial reconstitution workflow.",
      },
      {
        title: "Cold storage",
        body: "Store refrigerated according to the lot-specific handling guidance.",
      },
    ],
  },
  "retatrutide": {
    compoundLabel: "Retatrutide",
    descriptionHeading: "About Retatrutide",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    extraSection: {
      heading: "The three receptors, GLP-1R, GIPR, GCGR",
      body: "Retatrutide is investigated in laboratory research as a simultaneous agonist of three receptors from the metabolic-hormone / incretin signalling family: the glucagon-like peptide-1 receptor (GLP-1R), the glucose-dependent insulinotropic polypeptide receptor (GIPR), and the glucagon receptor (GCGR). This distinguishes it from single-receptor (GLP-1R only) and dual-receptor (GLP-1R + GIPR) research peptides in the same class.",
    },
    references: [
      {
        title: "Retatrutide (LY3437943), Triple hormone-receptor agonist research overview",
        source: "PubMed · Rosenstock et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/37356443/",
      },
      {
        title: "Pharmacological characterisation of LY3437943, a novel triple GLP-1, GIP and glucagon receptor agonist",
        source: "PubMed · Coskun et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/36423636/",
      },
      {
        title: "Retatrutide, Phase 2 laboratory research readouts in metabolic pathway signalling",
        source: "PubMed · Jastreboff et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/37356445/",
      },
      {
        title: "GLP-1, GIP and glucagon receptors, incretin and metabolic signalling review",
        source: "PubMed · Müller et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/31000876/",
      },
    ],
    relatedCompounds: [
      { slug: "retatrutide-pen", label: "Retatrutide 40mg Pen Kit" },
      { slug: "cagrilintide", label: "Cagrilintide" },
      { slug: "aod-9604", label: "AOD-9604" },
      { slug: "nad-plus", label: "NAD+" },
    ],
    relatedLearn: [
      { to: "/learn/retatrutide", label: "What is Retatrutide?" },
    ],
  },
  "retatrutide-pen": {
    compoundLabel: "Retatrutide Pen Kit",
    descriptionHeading: "About the Retatrutide 40mg Pen Kit",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    hidePenHowItWorks: true,
    hidePenFormatFaq: true,
    extraSection: {
      heading: "The three receptors, GLP-1R, GIPR, GCGR",
      body: "Retatrutide is investigated in laboratory research as a simultaneous agonist of three receptors from the metabolic-hormone / incretin signalling family: the glucagon-like peptide-1 receptor (GLP-1R), the glucose-dependent insulinotropic polypeptide receptor (GIPR), and the glucagon receptor (GCGR). This triple-receptor profile is what published research literature refers to when it describes retatrutide as a \"triple agonist\".",
    },
    penFormat: [
      {
        title: "Pre-filled",
        body: "Retatrutide is supplied pre-reconstituted in a research pen rather than as lyophilised raw powder.",
      },
      {
        title: "Dial-controlled",
        body: "Mechanical dial system for controlled laboratory dispensing.",
      },
      {
        title: "Compatible hardware",
        body: "Designed for the compatible Pen Kit hardware and consumables listed by PepDirect.",
      },
      {
        title: "Cold storage",
        body: "Follow the verified storage conditions displayed in the product specifications.",
      },
    ],
    references: [
      {
        title: "Retatrutide (LY3437943), Triple hormone-receptor agonist research overview",
        source: "PubMed · Rosenstock et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/37356443/",
      },
      {
        title: "Pharmacological characterisation of LY3437943, a novel triple GLP-1, GIP and glucagon receptor agonist",
        source: "PubMed · Coskun et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/36423636/",
      },
      {
        title: "GLP-1, GIP and glucagon receptors, incretin and metabolic signalling review",
        source: "PubMed · Müller et al.",
        url: "https://pubmed.ncbi.nlm.nih.gov/31000876/",
      },
    ],
    relatedCompounds: [
      { slug: "retatrutide", label: "Retatrutide Raw Vial" },
      { slug: "cagrilintide", label: "Cagrilintide" },
      { slug: "aod-9604", label: "AOD-9604" },
      { slug: "nad-plus", label: "NAD+" },
    ],
    relatedLearn: [
      { to: "/learn/retatrutide", label: "What is Retatrutide?" },
    ],
  },
  "cjc-1295-no-dac": {
    compoundLabel: "CJC-1295 No DAC",
    descriptionHeading: "About CJC-1295 No DAC",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    extraSection: {
      heading: "CJC-1295 No DAC vs CJC-1295 DAC",
      body: "No DAC, Modified GRF (1-29), without the Drug Affinity Complex. DAC, CJC-1295 variant incorporating a reactive Drug Affinity Complex intended to facilitate albumin association and extend pharmacokinetic persistence. The two are distinct chemical identities and research literature should not be interchanged between them.",
    },
    relatedCompounds: [
      { slug: "ipamorelin", label: "Ipamorelin" },
      { slug: "hulk", label: "HULK Raw Vial" },
      { slug: "cjc-1295-dac", label: "CJC-1295 w/ DAC" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-cjc-1295", label: "What is CJC-1295?" },
      { to: "/learn/cjc-1295-dac-vs-no-dac", label: "CJC-1295 DAC vs No DAC" },
      { to: "/learn/what-is-ipamorelin", label: "What is Ipamorelin?" },
    ],
  },
  "cjc-1295-dac": {
    compoundLabel: "CJC-1295 DAC",
    descriptionHeading: "About CJC-1295 DAC",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    extraSection: {
      heading: "CJC-1295 DAC vs CJC-1295 No DAC",
      body: "DAC, variant incorporating a Drug Affinity Complex designed to facilitate covalent association with circulating albumin. No DAC, Modified GRF (1-29), the same GHRH(1-29) sequence without the Drug Affinity Complex. The two are distinct chemical identities and research literature should not be interchanged between them.",
    },
    relatedCompounds: [
      { slug: "cjc-1295-no-dac", label: "CJC-1295 No DAC" },
      { slug: "ipamorelin", label: "Ipamorelin" },
      { slug: "hulk", label: "HULK Raw Vial" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-cjc-1295", label: "What is CJC-1295?" },
      { to: "/learn/cjc-1295-dac-vs-no-dac", label: "CJC-1295 DAC vs No DAC" },
      { to: "/learn/what-is-ipamorelin", label: "What is Ipamorelin?" },
    ],
  },
  "hulk": {
    compoundLabel: "HULK",
    descriptionHeading: "About HULK",
    researchContextHeading: "Ingredient Research Context",
    showComposition: true,
    ingredientContext: [
      {
        slug: "cjc-1295-no-dac",
        name: "CJC-1295 No DAC",
        body: "A synthetic analogue of growth hormone-releasing hormone (Modified GRF 1-29) studied for GHRH receptor signalling, growth-hormone pathway research and short-duration/pulsatile signalling research.",
      },
      {
        slug: "ipamorelin",
        name: "Ipamorelin",
        body: "A synthetic peptide studied as a growth-hormone secretagogue receptor (GHSR) agonist in secretagogue-pathway research and preclinical endocrine-response profiling.",
      },
    ],
    relatedCompounds: [
      { slug: "hulk-pen", label: "HULK Blend Pen Kit" },
      { slug: "cjc-1295-no-dac", label: "CJC-1295 No DAC" },
      { slug: "ipamorelin", label: "Ipamorelin" },
      { slug: "wolverine", label: "Wolverine Blend" },
    ],
    relatedLearn: [
      { to: "/learn/peptide-blends", label: "What are Peptide Blends?" },
      { to: "/learn/cjc-1295-dac-vs-no-dac", label: "CJC-1295 DAC vs No DAC" },
      { to: "/learn/what-is-ipamorelin", label: "What is Ipamorelin?" },
    ],
  },
  "hulk-pen": {
    compoundLabel: "HULK Pen Kit",
    descriptionHeading: "About the HULK Pen Kit",
    researchContextHeading: "Ingredient Research Context",
    hidePenHowItWorks: true,
    hidePenFormatFaq: true,
    penFormat: [
      {
        title: "Pre-filled",
        body: "Prepared in a pre-filled research-device format.",
      },
      {
        title: "Fixed-increment mechanism",
        body: "Mechanical click-increment device architecture.",
      },
      {
        title: "Two-component blend",
        body: "Contains CJC-1295 No DAC and Ipamorelin.",
      },
      {
        title: "Cold storage",
        body: "Handling and storage requirements follow the relevant product documentation.",
      },
    ],
    showComposition: true,
    ingredientContext: [
      {
        slug: "cjc-1295-no-dac",
        name: "CJC-1295 No DAC",
        body: "A synthetic analogue of growth hormone-releasing hormone (Modified GRF 1-29) studied for GHRH receptor signalling and growth-hormone pathway research.",
      },
      {
        slug: "ipamorelin",
        name: "Ipamorelin",
        body: "A synthetic peptide studied as a growth-hormone secretagogue receptor (GHSR) agonist in secretagogue-pathway research.",
      },
    ],
    relatedCompounds: [
      { slug: "hulk", label: "HULK Blend Raw Vial" },
      { slug: "cjc-1295-no-dac", label: "CJC-1295 No DAC" },
      { slug: "ipamorelin", label: "Ipamorelin" },
      { slug: "wolverine-pen", label: "Wolverine Pen Kit" },
    ],
    relatedLearn: [
      { to: "/learn/peptide-blends", label: "What are Peptide Blends?" },
      { to: "/learn/cjc-1295-dac-vs-no-dac", label: "CJC-1295 DAC vs No DAC" },
      { to: "/learn/what-is-ipamorelin", label: "What is Ipamorelin?" },
    ],
  },
  "pt-141": {
    compoundLabel: "PT-141",
    descriptionHeading: "About PT-141",
    researchContextHeading: "Melanocortin Research Context",
    extraSection: {
      heading: "PT-141 and Melanotan II",
      body: "PT-141 emerged from melanocortin-analogue research related to Melanotan II but is a distinct compound with its own chemical identity and receptor profile. Research findings for one should not be represented as findings for the other, and the two compounds are not interchangeable in the research literature.",
    },
    references: [
      {
        title: "Bremelanotide (PT-141) · PubChem CID 9941379",
        source: "PubChem (NCBI)",
        url: "https://pubchem.ncbi.nlm.nih.gov/compound/9941379",
      },
      {
        title: "Melanocortin receptors, MC3R / MC4R pharmacology review",
        source: "PubMed · Yang & Harmon",
        url: "https://pubmed.ncbi.nlm.nih.gov/17224219/",
      },
      {
        title: "Bremelanotide, selective melanocortin receptor agonist research overview",
        source: "PubMed · King, Hu, Molinoff",
        url: "https://pubmed.ncbi.nlm.nih.gov/17945228/",
      },
      {
        title: "Central melanocortin signalling, hypothalamic and limbic pathway research",
        source: "PubMed · Cone",
        url: "https://pubmed.ncbi.nlm.nih.gov/16311593/",
      },
    ],
    relatedCompounds: [
      { slug: "melanotan-2", label: "Melanotan II" },
      { slug: "melanotan-1", label: "Melanotan I" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-pt-141", label: "What is PT-141?" },
      { to: "/learn/melanotan-1-vs-2", label: "Melanotan I vs II" },
    ],
  },
  "ipamorelin": {
    compoundLabel: "Ipamorelin",
    descriptionHeading: "About Ipamorelin",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    references: [
      { title: "Ipamorelin · PubChem CID 9831659", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/9831659" },
      { title: "Ipamorelin, the first selective growth hormone secretagogue (Raun et al., 1998)", source: "PubMed · Eur J Endocrinol", url: "https://pubmed.ncbi.nlm.nih.gov/9849822/" },
      { title: "Growth-hormone secretagogue receptor (GHSR) pharmacology review", source: "PubMed · Kojima & Kangawa", url: "https://pubmed.ncbi.nlm.nih.gov/16109839/" },
    ],
    relatedCompounds: [
      { slug: "cjc-1295-no-dac", label: "CJC-1295 No DAC" },
      { slug: "cjc-1295-dac", label: "CJC-1295 DAC" },
      { slug: "hulk", label: "HULK Raw Vial" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-ipamorelin", label: "What is Ipamorelin?" },
      { to: "/learn/cjc-1295-dac-vs-no-dac", label: "CJC-1295 DAC vs No DAC" },
    ],
  },
  "tb-500": {
    compoundLabel: "TB-500",
    descriptionHeading: "About TB-500",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    extraSection: {
      heading: "TB-500 vs full-length thymosin beta-4",
      body: "TB-500 is an acetylated 7-residue peptide fragment (N-acetyl-LKKTETQ) corresponding to residues 17–23 of thymosin beta-4. Full-length thymosin beta-4 is a distinct 43-residue protein with a different CAS number, molecular formula and molecular weight. Research findings observed for the full-length protein do not automatically apply to the TB-500 fragment unless the fragment itself was studied.",
    },
    references: [
      { title: "TB-500 (acetylated Tβ4 17–23 fragment) · CAS 885340-08-9 identity record", source: "FDA GSRS / precisionFDA", url: "https://precision.fda.gov/ginas/app/ui/substances?q=885340-08-9" },
      { title: "Thymosin beta-4 (full-length), actin-sequestering research review", source: "PubMed · Goldstein et al.", url: "https://pubmed.ncbi.nlm.nih.gov/15922731/" },
      { title: "Thymosin β4 fragments and actin-binding research", source: "PubMed · Van Troys et al.", url: "https://pubmed.ncbi.nlm.nih.gov/8626711/" },
    ],
    relatedCompounds: [
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "glow", label: "GLOW" },
      { slug: "wolverine", label: "Wolverine" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-tb-500", label: "What is TB-500?" },
      { to: "/learn/bpc-157", label: "What is BPC-157?" },
      { to: "/learn/bpc-157-vs-tb-500", label: "BPC-157 vs TB-500" },
    ],
  },
  "selank": {
    compoundLabel: "Selank",
    descriptionHeading: "About Selank",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    references: [
      { title: "Selank · PubChem CID 11765600 (heptapeptide TKPRPGP)", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/11765600" },
      { title: "Regulatory peptide Selank, behavioural studies in animal models", source: "PubMed · Kozlovskii et al.", url: "https://pubmed.ncbi.nlm.nih.gov/17969460/" },
      { title: "Semax and Selank, comparative research on Russian regulatory peptides", source: "PubMed · Medvedev et al.", url: "https://pubmed.ncbi.nlm.nih.gov/23330133/" },
    ],
    relatedCompounds: [
      { slug: "semax", label: "Semax" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-selank", label: "What is Selank?" },
      { to: "/learn/semax-vs-selank", label: "Semax vs Selank" },
    ],
  },
  "cagrilintide": {
    compoundLabel: "Cagrilintide",
    descriptionHeading: "About Cagrilintide",
    researchContextHeading: "Cagrilintide Research Context",
    references: [
      { title: "Cagrilintide · PubChem CID 171397054", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/171397054" },
      { title: "Discovery of the Long-Acting Amylin Analogue Cagrilintide", source: "J. Med. Chem. · Andreassen et al.", url: "https://pubmed.ncbi.nlm.nih.gov/34677974/" },
      { title: "Structural and dynamic features of cagrilintide binding to calcitonin and amylin receptors", source: "Nature Communications · Cao et al.", url: "https://www.nature.com/articles/s41467-022-34964-5" },
    ],
    relatedCompounds: [
      { slug: "retatrutide", label: "Retatrutide" },
      { slug: "mots-c", label: "MOTS-C" },
      { slug: "aod-9604", label: "AOD-9604" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-cagrilintide", label: "What is Cagrilintide?" },
      { to: "/learn/retatrutide", label: "What is Retatrutide?" },
    ],
  },
  "mots-c": {
    compoundLabel: "MOTS-C",
    descriptionHeading: "About MOTS-C",
    researchContextHeading: "MOTS-C Research Context",
    references: [
      { title: "MOTS-c · PubChem CID 146675088 (16-aa mitochondrial-derived peptide)", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/146675088" },
      { title: "MOTS-c is an exercise-induced mitochondrial-encoded regulatory peptide (nuclear translocation)", source: "PubMed · Reynolds et al. (Cell Metabolism)", url: "https://pubmed.ncbi.nlm.nih.gov/29551591/" },
      { title: "Mitochondrial-derived peptides in metabolism and ageing, review", source: "PubMed · Kim et al.", url: "https://pubmed.ncbi.nlm.nih.gov/30057196/" },
    ],
    relatedCompounds: [
      { slug: "cagrilintide", label: "Cagrilintide" },
      { slug: "aod-9604", label: "AOD-9604" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-mots-c", label: "What is MOTS-C?" },
      { to: "/learn/nad-plus", label: "What is NAD+?" },
      { to: "/learn", label: "All Research Guides" },
    ],
  },
  "aod-9604": {
    compoundLabel: "AOD-9604",
    descriptionHeading: "About AOD-9604",
    researchContextHeading: "Preclinical & Laboratory Research Context",
    extraSection: {
      heading: "AOD-9604 and full-length growth hormone",
      body: "AOD-9604 is a peptide fragment / analogue derived from a C-terminal region associated with human growth hormone research. It is chemically distinct from intact human growth hormone. Research findings for full-length hGH should not automatically be assigned to AOD-9604, and the fragment does not necessarily act through all of the same receptor mechanisms as intact growth hormone.",
    },
    references: [
      { title: "AOD-9604 · PubChem CID 71300630 (peptide fragment)", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/71300630" },
      { title: "Metabolic studies of a synthetic lipolytic domain (AOD9604) of human growth hormone (animal / preclinical)", source: "PubMed · Ng et al.", url: "https://pubmed.ncbi.nlm.nih.gov/11060333/" },
      { title: "The effects of hGH and its lipolytic fragment (AOD9604) on lipid metabolism in obese mice and β3-AR knockout mice (animal / preclinical)", source: "PubMed · Heffernan et al.", url: "https://pubmed.ncbi.nlm.nih.gov/11414604/" },
    ],
    relatedCompounds: [
      { slug: "cagrilintide", label: "Cagrilintide" },
      { slug: "mots-c", label: "MOTS-C" },
      { slug: "ipamorelin", label: "Ipamorelin" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-aod-9604", label: "What is AOD-9604?" },
    ],
  },
  "wolverine": {
    compoundLabel: "Wolverine",
    descriptionHeading: "About Wolverine",
    researchContextHeading: "Ingredient Research Context",
    showComposition: true,
    ingredientContext: [
      { slug: "bpc-157", name: "BPC-157", body: "A synthetic pentadecapeptide investigated across cellular and animal tissue-repair, angiogenesis and gastrointestinal experimental models. See the BPC-157 product page for full mechanistic research context." },
      { slug: "tb-500", name: "TB-500", body: "The acetylated 17–23 fragment of thymosin beta-4 (N-acetyl-LKKTETQ). Distinct from the full-length 43-residue thymosin beta-4 protein, research on full-length Tβ4 should not be attributed to the TB-500 heptapeptide unless the fragment itself was studied." },
    ],
    relatedCompounds: [
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "tb-500", label: "TB-500" },
      { slug: "glow", label: "GLOW" },
      { slug: "klow", label: "KLOW" },
    ],
    relatedLearn: [
      { to: "/learn/peptide-blends", label: "What are Peptide Blends?" },
      { to: "/learn/bpc-157-vs-tb-500", label: "BPC-157 vs TB-500" },
      { to: "/learn/what-is-tb-500", label: "What is TB-500?" },
    ],
  },
  "wolverine-pen": {
    compoundLabel: "Wolverine Pen Kit",
    descriptionHeading: "About the Wolverine Pen Kit",
    researchContextHeading: "Ingredient Research Context",
    hidePenHowItWorks: true,
    hidePenFormatFaq: true,
    penFormat: [
      { title: "Pre-filled", body: "Supplied in a prepared research-device format." },
      { title: "Dial-controlled", body: "Mechanical dial system provides repeatable device positioning." },
      { title: "Compatible hardware", body: "Designed around compatible pen-format laboratory hardware." },
      { title: "Cold storage", body: "Storage conditions are governed by the current product documentation and lot-specific COA." },
    ],
    showComposition: true,
    ingredientContext: [
      { slug: "bpc-157", name: "BPC-157", body: "A synthetic pentadecapeptide investigated across cellular and animal tissue-repair, angiogenesis and gastrointestinal experimental models." },
      { slug: "tb-500", name: "TB-500", body: "The acetylated 17–23 fragment of thymosin beta-4. Chemically distinct from full-length thymosin beta-4 (43-residue protein). Full-length Tβ4 research findings should not be represented as evidence for the TB-500 fragment." },
    ],
    relatedCompounds: [
      { slug: "wolverine", label: "Wolverine Raw Vial" },
      { slug: "bpc-157", label: "BPC-157" },
      { slug: "tb-500", label: "TB-500" },
    ],
    relatedLearn: [
      { to: "/learn/peptide-blends", label: "What are Peptide Blends?" },
      { to: "/learn/bpc-157-vs-tb-500", label: "BPC-157 vs TB-500" },
      { to: "/learn/what-is-tb-500", label: "What is TB-500?" },
    ],
  },
  "melanotan-1": {
    compoundLabel: "Melanotan I",
    descriptionHeading: "About Melanotan I",
    researchContextHeading: "Melanocortin Research Context",
    extraSection: {
      heading: "Melanotan I and Melanotan II",
      body: "Melanotan I and Melanotan II are distinct peptide identities with different structures and receptor profiles. Melanotan I is a linear alpha-MSH analogue; Melanotan II is a cyclic peptide with broader melanocortin-receptor activity. Research findings for one should not automatically be assigned to the other.",
    },
    references: [
      { title: "Afamelanotide / Melanotan I · PubChem CID 16197727", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/16197727" },
      { title: "Melanocortin-1 receptor (MC1R) pharmacology research", source: "PubMed · Rees", url: "https://pubmed.ncbi.nlm.nih.gov/14514886/" },
      { title: "NDP-α-MSH structure-activity research (alpha-MSH analogues)", source: "PubMed · Sawyer et al.", url: "https://pubmed.ncbi.nlm.nih.gov/6968985/" },
    ],
    relatedCompounds: [
      { slug: "melanotan-2", label: "Melanotan II" },
      { slug: "pt-141", label: "PT-141 (Bremelanotide)" },
    ],
    relatedLearn: [
      { to: "/learn/melanotan-1-vs-2", label: "Melanotan I vs II" },
    ],
  },
  "melanotan-2": {
    compoundLabel: "Melanotan II",
    descriptionHeading: "About Melanotan II",
    researchContextHeading: "Melanocortin Research Context",
    extraSection: {
      heading: "Melanotan II and PT-141",
      body: "PT-141 (bremelanotide) emerged from related melanocortin-analogue research but is a distinct compound with its own chemical identity and pharmacology. The two should not be treated as interchangeable.",
    },
    references: [
      { title: "Melanotan II · PubChem CID 92432", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/92432" },
      { title: "Melanocortin receptor family review (MC1R/MC3R/MC4R)", source: "PubMed · Cone", url: "https://pubmed.ncbi.nlm.nih.gov/16311593/" },
      { title: "Cyclic α-MSH analogues, structure-activity research", source: "PubMed · Hruby et al.", url: "https://pubmed.ncbi.nlm.nih.gov/8577741/" },
    ],
    relatedCompounds: [
      { slug: "melanotan-1", label: "Melanotan I" },
      { slug: "pt-141", label: "PT-141 (Bremelanotide)" },
    ],
    relatedLearn: [
      { to: "/learn/melanotan-1-vs-2", label: "Melanotan I vs II" },
    ],
  },
  "dsip": {
    compoundLabel: "DSIP",
    descriptionHeading: "About DSIP",
    researchContextHeading: "DSIP Research Context",
    extraSection: {
      heading: "What does the evidence show?",
      body: "DSIP has a long experimental research history across cellular, animal and some human research contexts. Reported findings across the literature are not equivalent to established clinical efficacy, and research should be interpreted according to model type and study design. PepDirect does not represent DSIP as an approved treatment for sleep or any other condition.",
    },
    references: [
      { title: "DSIP / emideltide · PubChem CID 68816", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/68816" },
      { title: "DSIP and sleep, historical neuropeptide research review", source: "PubMed · Graf & Kastin", url: "https://pubmed.ncbi.nlm.nih.gov/6091043/" },
      { title: "Delta sleep-inducing peptide, chemistry and biology overview", source: "PubMed · Schoenenberger", url: "https://pubmed.ncbi.nlm.nih.gov/6320007/" },
    ],
    relatedCompounds: [
      { slug: "epithalon", label: "Epithalon" },
      { slug: "selank", label: "Selank" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-dsip", label: "What is DSIP?" },
      { to: "/learn", label: "All Research Guides" },
    ],
  },
  "epithalon": {
    compoundLabel: "Epithalon (Epitalon)",
    descriptionHeading: "About Epithalon / Epitalon",
    researchContextHeading: "Epithalon Research Context",
    extraSection: {
      heading: "Epitalon and Epithalon, are they the same?",
      body: "Yes. Both spellings are used interchangeably in research literature and chemical databases for the same AEDG (Ala-Glu-Asp-Gly) tetrapeptide identity. Epitalon has materially higher search demand; Epithalon is the spelling more commonly used in Russian-origin gerontology literature.",
    },
    references: [
      { title: "Epitalon / Epithalon (AEDG) · PubChem CID 219042", source: "PubChem (NCBI)", url: "https://pubchem.ncbi.nlm.nih.gov/compound/219042" },
      { title: "Effect of AEDG peptide on gene expression (preclinical)", source: "PubMed · Khavinson et al.", url: "https://pubmed.ncbi.nlm.nih.gov/23330141/" },
      { title: "Peptide regulation of telomerase, experimental research review", source: "PubMed · Khavinson & Anisimov", url: "https://pubmed.ncbi.nlm.nih.gov/12764199/" },
    ],
    relatedCompounds: [
      { slug: "nad-plus", label: "NAD+" },
      { slug: "dsip", label: "DSIP" },
    ],
    relatedLearn: [
      { to: "/learn/what-is-epitalon", label: "What is Epitalon (Epithalon)?" },
      { to: "/learn/nad-plus", label: "What is NAD+?" },
    ],
  },
};

function findEntry(slug, isRaw) {
  const prefix = SLUG_MATCH[slug];
  if (!prefix) return null;
  const list = isRaw ? content.raw.products : content.pen.products;
  return list.find((p) => p.name.startsWith(prefix)) || null;
}

// Section label, pure semantics upgrade. Renders <h2> for major PDP
// sections while keeping the exact same visual styling as the previous
// <p className="label-mono"> tag. Sitewide, so every product PDP now has
// a proper heading hierarchy (H1 = product title, H2 = each section)
// without changing pixel output.
function SectionLabel({ children, testId }) {
  return (
    <h2 className="label-mono text-brandblue" data-testid={testId}>
      {children}
    </h2>
  );
}

export default function ProductContent({ product }) {
  const isRaw = product.format === "raw";
  const isPen = product.format === "pen";

  const entry = useMemo(() => findEntry(product.slug, isRaw), [product.slug, isRaw]);
  const override = PDP_CONTENT_OVERRIDES[product.slug] || null;

  // Live product visibility. Shared cache with CartDrawer/CatalogPage
  // (["products-all"]) so this is a zero-network dedupe most of the time.
  // Used to filter relatedCompounds against the authoritative catalog so a
  // withdrawn/discontinued/hidden slug never surfaces as a crawlable
  // internal link, regardless of what the local overrides say.
  const { data: liveProducts } = useQuery({
    queryKey: ["products-all"],
    queryFn: async () => (await api.get("/products")).data,
    staleTime: 5 * 60 * 1000,
  });
  const liveSlugSet = useMemo(() => {
    if (!Array.isArray(liveProducts)) return null;
    return new Set(
      liveProducts
        .filter((p) => !p.hidden && !p.coming_soon)
        .map((p) => p.slug),
    );
  }, [liveProducts]);
  const visibleRelated = useMemo(() => {
    const list = override?.relatedCompounds || [];
    if (!liveSlugSet) return list; // pre-hydration: don't hide known chips
    return list.filter((r) => liveSlugSet.has(r.slug));
  }, [override, liveSlugSet]);

  if (!isRaw && !isPen) return null;

  const shared = isRaw ? content.raw.shared : content.pen.shared;
  if (!entry && !shared) return null;

  // Runtime-generated testing FAQ when the compound has an SEO override
  // AND the product record carries verified testing metadata. Kept out of
  // the JSON deliberately so the answer always reflects the current lot's
  // stored data (HPLC / LOT), never a stale hand-written string.
  const compoundLabel = override?.compoundLabel || SLUG_MATCH[product.slug];
  const dynamicFaq = [];
  if (override) {
    // Amount-list FAQ, only rendered on Raw Vial products that carry a
    // multi-variant amount selector (currently GHK-Cu). Reads the live
    // variant list so the answer never falls out of sync with the
    // amounts actually orderable on the page.
    if (isRaw && Array.isArray(product.variants) && product.variants.length > 1) {
      const amounts = product.variants
        .map((v) => v.label || (v.pack_size ? `${v.pack_size}mg` : null))
        .filter(Boolean);
      if (amounts.length > 0) {
        dynamicFaq.push({
          q: `What amounts of ${compoundLabel} does PepDirect supply?`,
          a: `PepDirect currently supplies ${compoundLabel} in the following amounts: ${amounts.join(" and ")}. The active selection on this page controls price and stock.`,
        });
      }
    }
    // Build each clause as a stand-alone sentence with its own capital so
    // parts.join('. ') doesn't produce lowercase-starting sentences.
    const parts = [];
    parts.push(
      product.hplc
        ? `${compoundLabel} is tested by HPLC to a purity of ${product.hplc}.`
        : `${compoundLabel} is tested by HPLC.`
    );
    if (product.lot) parts.push(`The current lot displayed on this page is ${product.lot}.`);
    parts.push("A lot-specific Certificate of Analysis is published on the product page before purchase and ships in the box.");
    dynamicFaq.push({
      q: `How is PepDirect ${compoundLabel} tested?`,
      a: parts.join(" "),
    });
  }

  const combinedFaq = [
    ...(entry?.compound_faq || []),
    ...dynamicFaq,
    ...(entry?.format_faq || []),
    ...(override?.hidePenFormatFaq ? [] : (shared?.format_faq || [])),
    ...(shared?.faq || []),
  ];

  const descHeading = override?.descriptionHeading || "Description";
  const researchHeading = override?.researchContextHeading || "Research Context";

  return (
    <section className="mt-14 space-y-10" data-testid="product-content">
      {/* Description */}
      {entry?.full && (
        <div data-testid="product-description">
          <SectionLabel testId="description-heading">§ {descHeading}</SectionLabel>
          <div className="mt-3 max-w-3xl space-y-3 text-base leading-relaxed text-ink">
            {entry.full.split(/\n\s*\n/).map((para, i) => (
              <p key={i}>{para.replace(/\n/g, " ")}</p>
            ))}
          </div>
        </div>
      )}

      {/* Optional extra prose block, currently used by GHK-Cu to
          distinguish the free tripeptide (GHK) from the copper complex
          (GHK-Cu). Rendered as its own H2 section so search engines and
          researchers see the identity clarification alongside the main
          description without polluting other slugs' pages. */}
      {override?.extraSection && (
        <div data-testid="product-extra-section">
          <SectionLabel>§ {override.extraSection.heading}</SectionLabel>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink">
            {override.extraSection.body}
          </p>
        </div>
      )}

      {/* How It Works, pen kits only. Suppressed per-product when the
          override sets hidePenHowItWorks (e.g. research-only pages where a
          human-use-style step sequence would misrepresent the product). */}
      {isPen && shared?.how_it_works && !override?.hidePenHowItWorks && (
        <div data-testid="product-how-it-works">
          <SectionLabel>§ How it works</SectionLabel>
          <ol className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {shared.how_it_works.steps.map((step, i) => (
              <li key={step} className="rounded-xl panel-warm bg-white p-4 text-center">
                <span className="block font-mono text-[10px] tracking-[0.16em] text-subtle">STEP {i + 1}</span>
                <span className="mt-2 block font-heading text-lg font-black text-ink">{step}</span>
              </li>
            ))}
          </ol>
          <p className="mt-3 font-mono text-xs text-subtle">{shared.how_it_works.footnote}</p>
        </div>
      )}

      {/* Pen Format, replaces the shared "How it works" step sequence on
          research-only pen pages. Describes device/format properties
          rather than a chronological usage flow. */}
      {isPen && override?.penFormat?.length > 0 && (
        <div data-testid="product-pen-format">
          <SectionLabel>§ Pen format</SectionLabel>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {override.penFormat.map((card) => (
              <div
                key={card.title}
                className="rounded-xl panel-warm bg-white p-5"
                data-testid={`pen-format-card-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-brandblue">{card.title}</p>
                <p className="mt-2 text-sm text-ink">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KLOW Composition, labelled section that lists the structured
          composition data (source: product.composition on the backend
          record). Rendered when the override opts in AND the product
          actually carries a composition array so we never invent data. */}
      {override?.showComposition && Array.isArray(product.composition) && product.composition.length > 0 && (
        <div data-testid="product-composition">
          <SectionLabel>§ {compoundLabel} Composition</SectionLabel>
          <div className="mt-4 rounded-xl panel-warm bg-white p-6">
            <ul className="space-y-2 text-sm text-ink" data-testid="composition-list">
              {product.composition.map((ing) => (
                <li key={`${ing.name}-${ing.amount}`} className="flex items-baseline justify-between gap-4 border-b border-line pb-2 last:border-none">
                  <span className="font-heading text-sm font-bold text-ink">
                    {ing.slug ? (
                      <Link
                        to={`/product/${ing.slug}`}
                        className="text-brandblue underline-offset-2 hover:underline"
                        data-testid={`composition-ingredient-link-${ing.slug}`}
                      >
                        {ing.name}
                      </Link>
                    ) : (
                      ing.name
                    )}
                  </span>
                  <span className="font-mono text-xs text-subtle">{ing.amount}</span>
                </li>
              ))}
            </ul>
            {(() => {
              // Derive the total amount from the active variant's pack_size
              // (which equals total mg for a blend) rather than summing the
              // string amounts, the DB variant is the source of truth.
              const totalMg = product.variants?.[0]?.pack_size;
              if (!totalMg) return null;
              return (
                <p className="mt-4 font-mono text-[11px] tracking-[0.14em] text-subtle" data-testid="composition-total">
                  TOTAL AMOUNT · {totalMg}mg
                </p>
              );
            })()}
          </div>
        </div>
      )}

      {/* Ingredient Research Context, only rendered for compounds that
          are themselves blends of separately-studied ingredients (KLOW).
          Each entry links to the ingredient's own PDP and is deliberately
          neutral about the blend itself so we don't imply blend-level
          evidence from single-ingredient findings. */}
      {override?.ingredientContext?.length > 0 && (
        <div data-testid="product-ingredient-context">
          <SectionLabel>§ Ingredient Research Context</SectionLabel>
          <p className="mt-3 max-w-3xl text-sm text-subtle">
            Each ingredient has been investigated separately in experimental research. Findings for an individual ingredient below do not demonstrate that the combined {compoundLabel} formulation produces the same effects.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {override.ingredientContext.map((ing) => (
              <div key={ing.slug} className="rounded-xl panel-warm bg-white p-5" data-testid={`ingredient-card-${ing.slug}`}>
                <Link
                  to={`/product/${ing.slug}`}
                  className="font-heading text-base font-black text-brandblue underline-offset-2 hover:underline"
                  data-testid={`ingredient-context-link-${ing.slug}`}
                >
                  {ing.name}
                </Link>
                <p className="mt-2 text-sm text-ink">{ing.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scientific Specifications */}
      {entry?.specs?.length > 0 && (
        <div data-testid="product-specs">
          <SectionLabel>§ Scientific Specifications</SectionLabel>
          <dl className="mt-4 grid grid-cols-1 gap-2 rounded-xl panel-warm bg-white p-6 sm:grid-cols-2">
            {entry.specs.map((row) => {
              const [k, ...rest] = row.split(":");
              const v = rest.join(":").trim();
              return (
                <div key={row} className="flex justify-between gap-4 border-b border-line pb-2 last:border-none">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.14em] text-subtle">{k.trim()}</dt>
                  <dd className="text-right font-mono text-xs text-ink">{v || "—"}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      )}

      {/* Blend Components, legacy plain-text ingredient list. Suppressed
          when an override renders the labelled Composition section
          (product-composition) with the same data, to avoid duplicating
          on-page content. */}
      {!override?.showComposition && entry?.components?.length > 0 && (
        <div data-testid="product-components">
          <SectionLabel>§ Blend Components</SectionLabel>
          <ul className="mt-3 space-y-1.5 text-sm text-ink">
            {entry.components.map((c) => (
              <li key={c} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brandblue" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Research Context */}
      {entry?.research_context?.length > 0 && (
        <div data-testid="product-research-context">
          <SectionLabel>§ {researchHeading}</SectionLabel>
          <ul className="mt-3 space-y-2 text-sm text-ink">
            {entry.research_context.map((r) => (
              <li key={r} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brandblue" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quality & Batch Testing, only rendered when a per-slug override
          exists AND the product record carries verified testing data (hplc,
          lot). Reuses the accordion/anchors already in the right rail
          rather than duplicating any PDFs. */}
      {override && (product.hplc || product.lot) && (
        <div data-testid="product-quality">
          <SectionLabel>§ Quality &amp; Batch Testing</SectionLabel>
          <div className="mt-3 max-w-3xl space-y-3 text-sm leading-relaxed text-ink">
            <p>
              Each batch of {compoundLabel} is independently tested before it ships.
              {product.hplc
                ? ` Purity is verified by HPLC (High-Performance Liquid Chromatography) at ${product.hplc}, and`
                : " Purity is verified by HPLC (High-Performance Liquid Chromatography), and"}
              {" "}the current lot displayed on this product page{product.lot ? ` (LOT ${product.lot})` : ""} corresponds to the certificate of analysis available under the <span className="font-semibold text-ink">Lab certificates</span> accordion on this page.
            </p>
            <p className="text-subtle">
              Lot-specific COA documentation is published here before purchase and ships in the box.
              To confirm a lot after delivery, visit <Link to="/verify" className="font-medium text-brandblue underline underline-offset-2">Verify your product</Link>.
            </p>
          </div>
        </div>
      )}

      {/* Research References, compact source list, opens in new tab safely */}
      {override?.references?.length > 0 && (
        <div data-testid="product-references">
          <SectionLabel>§ Research References</SectionLabel>
          <ul className="mt-3 max-w-3xl space-y-2.5 text-sm text-ink">
            {override.references.map((ref) => (
              <li key={ref.url} className="flex flex-col gap-0.5 rounded-lg panel-warm bg-white p-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="font-medium text-brandblue underline underline-offset-2 hover:text-brandbluedark"
                  data-testid={`reference-link-${ref.url.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                >
                  {ref.title}
                </a>
                <span className="font-mono text-[11px] tracking-[0.06em] text-subtle">{ref.source}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* FAQ (compound-specific + dynamic testing + format-specific + shared) */}
      {combinedFaq.length > 0 && (
        <div data-testid="product-faq">
          <SectionLabel>§ FAQ</SectionLabel>
          <dl className="mt-4 divide-y divide-line rounded-xl panel-warm bg-white">
            {combinedFaq.map((f, i) => (
              <div key={i} className="p-5">
                <dt className="font-heading text-sm font-bold text-ink">{f.q}</dt>
                <dd className="mt-1.5 text-sm text-subtle">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Intended Use */}
      {shared?.intended_use && (
        <div data-testid="product-intended-use">
          <SectionLabel>§ Intended Use</SectionLabel>
          <p className="mt-3 max-w-3xl text-sm text-ink">{shared.intended_use}</p>
        </div>
      )}

      {/* Related Research Compounds, internal linking for research-topic
          navigation. Purely a directory: no dosing or stack claims.
          Filtered against /api/products so withdrawn/hidden slugs never
          surface as a crawlable link. */}
      {(visibleRelated.length > 0 || override?.relatedLearn?.length > 0) && (
        <div data-testid="product-related-compounds">
          <SectionLabel>§ Related Research Compounds</SectionLabel>
          {visibleRelated.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2">
              {visibleRelated.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/product/${r.slug}`}
                    className="inline-flex items-center rounded-full border border-brandblue/30 bg-white px-3.5 py-1.5 font-mono text-[12px] font-medium tracking-[0.04em] text-brandblue hover:bg-brandblue/8"
                    data-testid={`related-compound-${r.slug}`}
                  >
                    {r.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {override?.relatedLearn?.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2" data-testid="product-related-learn">
              {override.relatedLearn.map((r) => (
                <li key={r.to}>
                  <Link
                    to={r.to}
                    className="inline-flex items-center rounded-full border border-brandblue/30 bg-brandblue/5 px-3.5 py-1.5 font-mono text-[12px] font-medium tracking-[0.04em] text-brandblue hover:bg-brandblue/10"
                    data-testid={`related-learn-${r.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                  >
                    Learn: {r.label} →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Legal Disclaimer */}
      {shared?.legal && (
        <div data-testid="product-legal">
          <SectionLabel>§ Legal Disclaimer</SectionLabel>
          <p className="mt-3 max-w-3xl text-sm text-ink">{shared.legal}</p>
        </div>
      )}

      {/* Terms of Sale */}
      {shared?.terms && (
        <div data-testid="product-terms">
          <SectionLabel>§ Terms of Sale</SectionLabel>
          <p className="mt-3 max-w-3xl text-sm text-ink">{shared.terms}</p>
        </div>
      )}
    </section>
  );
}
